/**
 * BookingConfirmationPage Component
 *
 * Displays booking confirmation after successful submission.
 */

import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { PublicLayout, Container } from '../../components/layout';
import { useBookingDetails } from './hooks';

// Format date safely
const formatDate = (date: Date | unknown): string => {
  if (!date) return 'N/A';

  // Handle Firestore Timestamp
  if (date && typeof date === 'object' && 'toDate' in date && typeof (date as { toDate: () => Date }).toDate === 'function') {
    date = (date as { toDate: () => Date }).toDate();
  }

  // Ensure it's a valid Date
  const d = date instanceof Date ? date : new Date(date as string | number);

  // Check if date is valid
  if (isNaN(d.getTime())) {
    return 'N/A';
  }

  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// Format currency
const formatCurrency = (amount: number): string => {
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

export const BookingConfirmationPage: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const { booking, isLoading } = useBookingDetails(bookingId);

  if (isLoading) {
    return (
      <PublicLayout>
        <Container className="py-12">
          <ConfirmationSkeleton />
        </Container>
      </PublicLayout>
    );
  }

  if (!booking) {
    return (
      <PublicLayout>
        <Container className="py-12 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Booking not found</h1>
          <p className="text-gray-600 mb-4">We couldn't find this booking.</p>
          <Link to="/" className="text-primary-600 hover:underline">
            Return to home
          </Link>
        </Container>
      </PublicLayout>
    );
  }

  const isConfirmed = booking.status === 'confirmed';

  return (
    <PublicLayout>
      <Container className="py-12">
        <div className="max-w-2xl mx-auto text-center">
          {/* Success Icon */}
          <div
            className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${
              isConfirmed ? 'bg-green-100' : 'bg-amber-100'
            }`}
          >
            {isConfirmed ? (
              <CheckIcon className="w-10 h-10 text-green-600" />
            ) : (
              <ClockIcon className="w-10 h-10 text-amber-600" />
            )}
          </div>

          {/* Title */}
          <h1 className="text-3xl font-bold text-gray-900 mb-3">
            {isConfirmed ? 'Booking Confirmed!' : 'Request Submitted!'}
          </h1>

          {/* Subtitle */}
          <p className="text-lg text-gray-600 mb-8">
            {isConfirmed
              ? "Your accommodation is booked. We've sent a confirmation to your email."
              : "The host will review your request. You'll hear back within 24 hours."}
          </p>

          {/* Booking Card */}
          <div className="bg-white border border-gray-200 rounded-2xl p-6 text-left mb-8">
            {/* Listing Preview */}
            {booking.listing && (
              <div className="flex gap-4 pb-6 border-b border-gray-200 mb-6">
                <img
                  src={booking.listing.photo}
                  alt={booking.listing.title}
                  className="w-24 h-24 object-cover rounded-lg"
                />
                <div>
                  <h2 className="font-semibold text-gray-900">{booking.listing.title}</h2>
                  <p className="text-sm text-gray-500">{booking.listing.location}</p>
                </div>
              </div>
            )}

            {/* Booking Details */}
            <h3 className="font-semibold text-gray-900 mb-4">Booking Details</h3>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">Confirmation code</dt>
                <dd className="font-mono font-medium text-gray-900">{bookingId}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Status</dt>
                <dd>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      isConfirmed
                        ? 'bg-green-100 text-green-700'
                        : 'bg-amber-100 text-amber-700'
                    }`}
                  >
                    {isConfirmed ? 'Confirmed' : 'Pending'}
                  </span>
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Check-in</dt>
                <dd className="font-medium text-gray-900">{formatDate(booking.checkIn)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Check-out</dt>
                <dd className="font-medium text-gray-900">{formatDate(booking.checkOut)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Guests</dt>
                <dd className="font-medium text-gray-900">
                  {booking.guests.adults} adult{booking.guests.adults !== 1 ? 's' : ''}
                  {booking.guests.children > 0 &&
                    `, ${booking.guests.children} child${booking.guests.children !== 1 ? 'ren' : ''}`}
                  {booking.guests.infants > 0 &&
                    `, ${booking.guests.infants} infant${booking.guests.infants !== 1 ? 's' : ''}`}
                </dd>
              </div>
              <div className="flex justify-between pt-3 border-t border-gray-200">
                <dt className="text-gray-900 font-semibold">Total</dt>
                <dd className="font-semibold text-gray-900">
                  {formatCurrency(booking.pricing.totalAmount)}
                </dd>
              </div>
            </dl>
          </div>

          {/* Next Steps */}
          <div className="bg-gray-50 rounded-2xl p-6 text-left mb-8">
            <h3 className="font-semibold text-gray-900 mb-4">Next Steps</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="flex items-center justify-center w-6 h-6 bg-primary-100 text-primary-600 rounded-full text-sm font-medium flex-shrink-0">
                  1
                </span>
                <span className="text-gray-700">
                  {isConfirmed
                    ? 'Complete your payment using the method you selected'
                    : 'Wait for the host to accept your booking request'}
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex items-center justify-center w-6 h-6 bg-primary-100 text-primary-600 rounded-full text-sm font-medium flex-shrink-0">
                  2
                </span>
                <span className="text-gray-700">
                  {isConfirmed
                    ? "You'll receive detailed check-in instructions from the host"
                    : "Once confirmed, you'll receive payment instructions"}
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex items-center justify-center w-6 h-6 bg-primary-100 text-primary-600 rounded-full text-sm font-medium flex-shrink-0">
                  3
                </span>
                <span className="text-gray-700">
                  Contact the host with any questions through the DARAK app
                </span>
              </li>
            </ul>
          </div>

          {/* Payment Reminder */}
          {booking.paymentStatus === 'pending' && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8 text-left">
              <div className="flex items-start gap-3">
                <AlertIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-amber-800">Payment Required</h4>
                  <p className="text-sm text-amber-700 mt-1">
                    Please complete your payment within 24 hours to secure your booking.
                  </p>
                  {isConfirmed && (
                    <Link
                      to={`/booking/${bookingId}/pay`}
                      className="mt-3 inline-block px-5 py-2.5 bg-primary-500 text-white font-semibold rounded-lg hover:bg-primary-600 transition-colors text-sm"
                    >
                      Complete Payment
                    </Link>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/dashboard/bookings"
              className="px-6 py-3 bg-primary-500 text-white font-semibold rounded-xl hover:bg-primary-600 transition-colors"
            >
              View My Bookings
            </Link>
            <Link
              to="/"
              className="px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </Container>
    </PublicLayout>
  );
};

// Loading Skeleton
const ConfirmationSkeleton: React.FC = () => (
  <div className="max-w-2xl mx-auto animate-pulse">
    <div className="flex flex-col items-center">
      <div className="w-20 h-20 bg-gray-200 rounded-full mb-6" />
      <div className="h-10 bg-gray-200 rounded w-64 mb-3" />
      <div className="h-6 bg-gray-200 rounded w-96 mb-8" />
      <div className="w-full h-72 bg-gray-200 rounded-2xl mb-8" />
      <div className="w-full h-40 bg-gray-200 rounded-2xl" />
    </div>
  </div>
);

// Icons
const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const ClockIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const AlertIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
    />
  </svg>
);

export default BookingConfirmationPage;
